import React from 'react';
import intl from 'react-intl-universal';
import {
  Typography,
  tailwindOverride,
  FormItem,
  NumericInput,
} from '@getsynapse/design-system';
import SchedulePreview from './SchedulePreview';

interface TimelineProps {
  startDay: string;
  duration: string;
  onStartDayChange: (value: string) => void;
  onDurationChange: (value: string) => void;
  isPartial?: boolean;
}

// Prevent typing characters that would produce a negative, decimal or
// exponent value — only whole positive numbers are allowed in the timeline.
const blockInvalidNumberKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (['-', '+', '.', 'e', 'E'].includes(e.key)) {
    e.preventDefault();
  }
};

// A whole positive integer with no leading zero. Validated against the raw
// string (not Number()) so pasted/dropped values like "1e3", "+2" or "1.0" —
// which the keydown block can't catch — are rejected too.
const POSITIVE_INTEGER = /^[1-9]\d*$/;

const Timeline = ({
  startDay,
  duration,
  onStartDayChange,
  onDurationChange,
  isPartial,
}: TimelineProps) => {
  const startDayValue = startDay === '' ? '' : +startDay + 1;

  return (
    <div className={tailwindOverride('flex flex-col gap-y-4')}>
      <div className={tailwindOverride('flex flex-col gap-y-1')}>
        <Typography variant='h6' weight='medium'>
          {intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.TITLE'
          )}
        </Typography>
        <Typography variant='body2' className='text-primary-dark'>
          {intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.DESCRIPTION'
          )}
        </Typography>
      </div>
      <div className='flex w-full gap-x-6'>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.START_DAY_LABEL'
          )}
          helpText={intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.START_DAY_HELPER'
          )}
          labelProps={{ className: 'mb-1' }}
          className='w-1/2'
        >
          <div className='flex gap-x-2 items-center'>
            <NumericInput
              step='1'
              value={startDayValue}
              onKeyDown={blockInvalidNumberKeys}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const day = e.target.value;
                if (day === '') {
                  onStartDayChange('');
                  return;
                }
                // Reject 0, negatives, decimals and exponent/sign notation:
                // returning without propagating leaves the field at its last
                // valid value (React restores the controlled value). Stored
                // value is 0-indexed (Day 1 -> "0").
                if (!POSITIVE_INTEGER.test(day)) {
                  return;
                }
                onStartDayChange((Number(day) - 1).toString());
              }}
              data-testid='timeline__start-day'
              id='timeline__start-day'
            />
            <span className='text-sm text-primary-dark'>
              {intl.get(
                'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.START_DAY_SUFFIX'
              )}
            </span>
          </div>
        </FormItem>
        <FormItem
          label={intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.DURATION_LABEL'
          )}
          helpText={intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.DURATION_HELPER'
          )}
          labelProps={{ className: 'mb-1' }}
          className='w-1/2'
        >
          <div className='flex gap-x-2 items-center'>
            <NumericInput
              step='1'
              value={duration}
              onKeyDown={blockInvalidNumberKeys}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                if (value === '') {
                  onDurationChange('');
                  return;
                }
                // Reject 0, negatives, decimals and exponent/sign notation:
                // returning without propagating leaves the field at its last
                // valid value (React restores the controlled value).
                if (!POSITIVE_INTEGER.test(value)) {
                  return;
                }
                onDurationChange(value);
              }}
              data-testid='timeline__duration'
              id='timeline__duration'
            />
            <span className='text-sm text-primary-dark'>
              {intl.get(
                'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.DURATION_SUFFIX',
                { dur: duration }
              )}
            </span>
          </div>
        </FormItem>
      </div>
      {isPartial && (
        <span className='text-xs text-error' role='alert'>
          {intl.get(
            'SETTINGS_PAGE.TASK_BUNDLE_PAGE.BUILD_SECTION.TIMELINE.PARTIAL_ERROR'
          )}
        </span>
      )}
      {startDay !== '' && duration !== '' && (
        <SchedulePreview startDay={startDay} duration={duration} />
      )}
    </div>
  );
};

export default Timeline;
